import { PageHeader } from "@shared/components/PageHeader";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@shared/ui/dialog";
import { SearchInput } from "@shared/components/SearchInput";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Search } from "lucide-react";
import React, { useState } from "react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useSubAccountConsumerSearch, useAddSubAccount, useGetContractsDuringAddList } from "@features/cx/consumerManagement/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// ─── Types ─────────────────────────────────────────────────────────────────
interface AddSubAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (consumer: any) => void;
  consumerId: string;
  subCategory: string;
  contractData: any;
}

// ─── Mock result for UI demonstration ──────────────────────────────────────
const MOCK_RESULT = {
  consumerNo: "50127",
  firstName: "Samoa",
  lastName: "Business Center - Branch C",
  categoryDisplay: "Commercial",
  subCategoryDisplay: "Retail",
  contactNumber: "+685 23000",
  email: "branch.c@samoabusiness.ws",
};

// ─── Component ─────────────────────────────────────────────────────────────
export const AddSubAccountModal: React.FC<AddSubAccountModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  consumerId,
  subCategory,
  contractData,
}) => {
  const [selectedConsumer, setSelectedConsumer] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  console.log("AddSubAccountModal Props:", { consumerId, isOpen });

  const { refetch: searchConsumers } = useSubAccountConsumerSearch({
    remoteUtilityId: getRemoteUtilityId().toString(),
    searchData: searchTerm,
  });

  const { mutate: addSubAccount, isPending: isAdding } = useAddSubAccount();


  // ── Build form via FormService ─────────────────────────────────────────
  const searchFields: FormField[] = [
    {
      name: "search_account",
      type: "text",
      label: "Search Account Number",
      placeholder: "e.g. 50127",

      // ── customValidation: numeric digits only ───────────────────────────
      customValidation: (value: string) => {
        if (!value || value.trim() === "") return "Account number is required";
        if (!/^\d+$/.test(value.trim()))
          return "Account number should contain only digits";
        return true;
      },

      // ── customRender: override the input with a search-styled input ──────
      customRender: (_field, form, error) => {
        // Mock search function matching SearchInput expectations
        const onSearch = async (term: string) => {
          setSearchTerm(term);
          const { data } = await searchConsumers();
          return (
            data?.result?.map((item: any) => ({
              id: item.id,
              label: item.consumerNo,
              description: `${item.firstName} ${item.lastName}`,
              data: item,
            })) || []
          );
        };

        return (
          <div className="space-y-3">
            {/* ── Search Input Component ─────────────────────────── */}
            <SearchInput
              placeholder="e.g. 50127"
              minSearchLength={3}
              onSearch={onSearch}
              onSelect={(result) => {
                form.setValue("search_account", result.label, {
                  shouldValidate: true,
                });
                setSelectedConsumer(result.data);
              }}
              inputClassName="h-10 text-sm"
              className="w-full"
            />
            {/* ── Validation error ──────────────────────────────────────── */}
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        );
      },
    },
  ];

  const form = FormService.createForm({
    fields: searchFields,
    mode: "onChange",
  });

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleCancel = () => {
    setSelectedConsumer(null);
    form.reset();
    onClose();
  };

  const handleAdd = () => {
    console.log("handleAdd check:", {
      propConsumerId: consumerId,
      selectedConsumerId: selectedConsumer?.id,
      selectedConsumerData: selectedConsumer,
    });
    if (!selectedConsumer) return;
    console.log("Adding sub-account with payload:", {
      remote_utility_id: 699,
      consumer_id: Number(consumerId),
      sub_account_id: selectedConsumer.id,
    });
    addSubAccount(
      {
        remote_utility_id: 699,
        consumer_id: Number(consumerId),
        sub_account_id: selectedConsumer.id,
      },
      {
        onSuccess: () => {
          onAdd(selectedConsumer);
          onClose();
        },
      }
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-lg bg-white rounded-lg shadow-xl border p-0 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <PageHeader
            title="Add Sub Account"
            subtitle="Search by account number, select it, then submit to add."
          />
        </div>

        {/* Body */}
        <div className="px-6 pt-4 pb-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Search field via DynamicForm + customRender */}
          <DynamicForm
            form={form}
            fields={searchFields}
            config={{
              layout: "vertical",
              classes: FIELD_CLASSES,
              showSubmitButton: false,
              showResetButton: false,
            }}
          />

          {/* Selected Account — ValueLabel grid */}
          {selectedConsumer && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Selected Account
                </p>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-5 p-5">
                <ValueLabel
                  label="Account No"
                  value={selectedConsumer.consumer_no || selectedConsumer.consumerNo}
                />
                <ValueLabel
                  label="Name"
                  value={`${selectedConsumer.first_name || selectedConsumer.firstName} ${selectedConsumer.last_name || selectedConsumer.lastName}`}
                />
                <ValueLabel
                  label="Category"
                  value={selectedConsumer.category_display || selectedConsumer.categoryDisplay}
                />
                <ValueLabel
                  label="Sub Category"
                  value={selectedConsumer.sub_category_display || selectedConsumer.subCategoryDisplay}
                />
                <ValueLabel
                  label="Phone"
                  value={selectedConsumer.contact_number || selectedConsumer.contactNumber}
                />
                <ValueLabel label="Email" value={selectedConsumer.email} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3 bg-gray-50 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="px-5 h-9 text-sm text-gray-700 bg-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!selectedConsumer || isAdding}
            className="px-6 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
          >
            {isAdding ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
